import { duplicateCase } from '@/api/cases';
import { createPathway, patchPathway } from '@/api/pathways';
import { isBuildPath } from '@/utils/miscUtils';
import PropTypes from 'prop-types';
import { Link, useLoaderData, useActionData, useParams, redirect, useSubmit, useRouteLoaderData } from 'react-router';
import SubmitButton from '../core/form/SubmitButton';
import { FormProvider, get, useForm } from 'react-hook-form';
import Tooltip from '../core/Tooltip';
import InputBase from '../core/form/InputBase';
import LocationBase from '../core/form/LocationBase';
import SelectBase from '../core/form/SelectBase';
import ProjectTargetField from '../projects/ProjectTargetField';

const paramTypes = {
  numeric: InputBase,
  location: LocationBase,
  options: SelectBase,
};

const filteredParams = {
  analysis_start_year: true,
  name: true,
  default_location: true,
  data_version: true,
};

const paramTypeConfig = {
  numeric: (name, param, register) => ({
    ...register(name, {
      valueAsNumber: true,
      required: `${param.label} is required`,
    }),
  }),
  location: (name, param) => ({
    name,
    placeholder: param.placeholder,
  }),
  options: (name, _param, register, paramOptions) => ({
    ...register(name),
    options: (paramOptions ?? []).map(option =>
      option !== null && typeof option === 'object'
        ? { value: option.value, label: option.label ?? option.value }
        : { value: option, label: option },
    ),
    isDefault: true,
  }),
};

const defaultValues = {
  analysis_start_year: new Date().getFullYear(),
};

const EditPathwayModal = ({ title }) => {
  const { pathway, projects, projectsNextPage, params } = useLoaderData() ?? {};
  const { pathwayParams } = useRouteLoaderData('root');
  const methods = useForm();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;
  const submit = useSubmit();
  const actionErrors = useActionData();
  const name = pathway?.name ?? '';
  const caseId = pathway?.case_id ?? '';
  const { projectId } = useParams();
  const hasProjectPicker = !!projects?.length;
  const defaultName = hasProjectPicker ? `${name} [duplicate]` : name;

  const onSubmit = (data, event) => {
    event.preventDefault();
    const payload = data.params
      ? {
          ...data,
          params: Object.fromEntries(
            Object.entries(data.params).filter(([, param]) => param?.primary?.value !== undefined),
          ),
        }
      : data;
    submit(payload, {
      method: 'put',
      encType: 'application/json',
    });
  };

  const renderParam = (paramKey, param) => {
    const ParamComponent = paramTypes[param.type];
    if (!ParamComponent) return null;

    const defaultValue =
      params?.[paramKey]?.primary?.value ??
      params?.[paramKey]?.primary?.default ??
      pathwayParams[paramKey]?.default ??
      defaultValues[paramKey];
    const fieldName = `params.${paramKey}.primary.value`;
    const paramOptions = params?.[paramKey]?.options ?? param.options;
    const config = paramTypeConfig[param.type]?.(fieldName, param, register, paramOptions) ?? {};
    const error = get(errors, fieldName);

    const isDisabled = paramKey === 'data_version' && !!params;

    return (
      <label key={paramKey} className="form-control w-full">
        <div className="label">
          <div className="flex items-center gap-2">
            <span className="label-text">{param.label}</span>
            <Tooltip label={param.label} text={param.tooltip} source={param.source} />
          </div>
        </div>
        <ParamComponent
          {...config}
          className={param.type === 'options' ? 'select-sm w-full' : 'input input-bordered input-sm w-full'}
          hasError={!!error}
          defaultValue={defaultValue}
          value={param.type === 'location' ? defaultValue : undefined}
          disabled={isDisabled}
        />
        <div className="label">
          <span className="label-text-alt text-error">{error?.message}</span>
        </div>
      </label>
    );
  };

  return (
    <FormProvider {...methods}>
      <dialog className="modal" open>
        <div className="modal-box">
          <form onSubmit={handleSubmit(onSubmit)}>
            <h3 className="font-bold text-lg">{title}</h3>
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Name</span>
              </div>
              <input
                type="text"
                {...register('name')}
                required
                placeholder="Enter pathway name"
                className="input input-bordered input-sm w-full"
                defaultValue={defaultName}
              />
              <div className="label">
                <span className="label-text-alt text-error">{actionErrors?.name}</span>
              </div>
              <input type="hidden" {...register('curName')} defaultValue={name} />
              <input type="hidden" {...register('caseId')} defaultValue={caseId} />
            </label>
            {!hasProjectPicker && (
              <>
                {Object.entries(pathwayParams)
                  .filter(([key]) => filteredParams[key])
                  .map(([key, param]) => renderParam(key, param))}
              </>
            )}
            {hasProjectPicker && (
              <ProjectTargetField
                defaultProjectId={projectId}
                initialNextPage={projectsNextPage}
                initialProjects={projects}
                inputProps={register('project_id')}
                onChange={project => setValue('project_id', project.value)}
              />
            )}
            <div className="modal-action">
              <Link className="btn btn-sm btn-secondary" to={-1} replace>
                Cancel
              </Link>
              <SubmitButton label="Save" />
            </div>
          </form>
        </div>
      </dialog>
    </FormProvider>
  );
};

EditPathwayModal.propTypes = {
  title: PropTypes.string,
};

EditPathwayModal.addAction = async ({ request, params }) => {
  const { projectId } = params;
  const pathway = await request.json();
  const buildPath = isBuildPath(request.url);
  const actionPath = buildPath ? `/projects/${projectId}/build/pathways` : `/projects/${projectId}/dashboard`;

  try {
    const { data } = await createPathway(projectId, pathway);
    return redirect(buildPath ? `${actionPath}/${data.id}/${data.case_id}` : actionPath);
  } catch (error) {
    return error.response.data;
  }
};

EditPathwayModal.editAction = async ({ request, params }) => {
  const { projectId, pathwayId, caseId } = params;
  const pathway = await request.json();
  const matchBuild = isBuildPath(request.url);
  const actionPath = matchBuild ? `/projects/${projectId}/build` : `/projects/${projectId}/dashboard`;

  try {
    await patchPathway(pathwayId, pathway);
    return redirect(matchBuild ? `${actionPath}/pathways/${pathwayId}/${caseId}` : actionPath);
  } catch (error) {
    return error.response.data;
  }
};

EditPathwayModal.duplicateAction = async ({ request, params }) => {
  const { pathwayId, projectId } = params;
  const pathway = await request.json();
  const { name, curName, caseId, project_id } = pathway;
  const matchBuild = isBuildPath(request.url);
  const actionPath = matchBuild ? `/projects/${project_id}/build` : `/projects/${project_id}/dashboard`;

  if (name === curName && project_id === projectId) {
    return { name: 'Please choose different name' };
  }

  try {
    const { data } = await duplicateCase(caseId, { name, project_id });
    return redirect(matchBuild ? `${actionPath}/pathways/${data.pathway_id}/${data.id}` : actionPath);
  } catch {
    return redirect(
      matchBuild ? `${actionPath}/pathways/${pathwayId}/duplicate` : `${actionPath}/${pathwayId}/duplicate`,
    );
  }
};

export default EditPathwayModal;
